import { PDFDocument } from "pdf-lib";
import { deduplicateC1PdfImages } from "../../ConceptOne/src/lib/C1_PdfImageDeduplication";
import { resolvePdfDocumentBrand } from "./PdfDocumentHeader";
import { ESTADA_PDF_COVER_LOGO } from "../../ConceptOne/src/C1_Const";
import type { PdfExportConfig, PdfChapterId } from "./PdfExportConfig";
import { addC1PdfOutlines, addC1PdfPageLinks } from "../../ConceptOne/src/lib/C1_PdfOutlines";
import { createC1PdfTableOfContentsBlob } from "../../ConceptOne/src/lib/C1_PdfTableOfContents";
import { restorePdfConfig, selectPdfChapter, availablePdfConfig, pdfChapterTitle, type PdfApplicationMode } from "./PdfExportConfig";
import { createC1MarkdownCoverPdfBlob } from "../../ConceptOne/src/raumbuch/C1_RaumbuchCoverMarkdown";
import {
  C1_CHAPTER_COVER_MARKDOWN,
  C1_COVER_MARKDOWN,
  getC1DocumentationMarkdown,
} from "../../ConceptOne/src/raumbuch/C1_RaumbuchCoverMarkdown";
import type { C1_PdfChapterArtifact } from "../../ConceptOne/src/lib/C1_PdfChapter";
import { createC1ButtonListPdf } from "../../ConceptOne/src/plan/C1_ButtonListPdf";
import { createBillOfMaterialsPdf } from "./createBillOfMaterialsPdf";
import { c1PdfFileName } from "../../ConceptOne/src/lib/C1_PdfFileName";
import { createGeneralNotesPdf } from "./createGeneralNotesPdf";
import { addDocumentPageNumbers } from "./pdfDocumentPageNumbers";

type Chapter = { id: PdfChapterId; title: string; artifact: C1_PdfChapterArtifact };

export async function createProjectPdf({
  config: inputConfig,
  mode,
  projectName,
  chapters: preparedChapters,
  onProgress,
  singleChapterId,
}: {
  mode: PdfApplicationMode;
  projectName: string;
  config: PdfExportConfig;
  chapters: Chapter[];
  onProgress?: (progress: number, message: string) => void;
  singleChapterId?: PdfChapterId;
}): Promise<void> {
  const config = availablePdfConfig(selectPdfChapter(restorePdfConfig(mode, inputConfig), singleChapterId), state);
  const buttonListArtifact = config.chapters.some((item) => item.enabled && item.id === "buttonList")
    ? await createC1ButtonListPdf(projectName)
    : undefined;
  const billOfMaterialsConfig = config.chapters.find((item) => item.enabled && item.id === "billOfMaterials");
  const billOfMaterialsArtifact = billOfMaterialsConfig
    ? await createBillOfMaterialsPdf(
        state,
        {
          mode: billOfMaterialsConfig.billOfMaterialsMode ?? "total",
          usage: billOfMaterialsConfig.billOfMaterialsUsage ?? false,
          distinguishLightTypes: billOfMaterialsConfig.billOfMaterialsLightTypes ?? false,
          includeWithoutProducts: billOfMaterialsConfig.billOfMaterialsWithoutProducts ?? true,
        },
        projectName,
      )
    : undefined;
  const chapters = config.chapters
    .filter((item) => item.enabled)
    .flatMap((item, index) => {
      const title = `Kapitel ${index + 1} · ${pdfChapterTitle(item)}`;
      const chapterLabel = { title, chapterNumber: index + 1, chapterTitle: pdfChapterTitle(item) };
      if (item.id === "buttonList" && buttonListArtifact) return [{ id: item.id, ...chapterLabel, artifact: buttonListArtifact }];
      if (item.id === "billOfMaterials" && billOfMaterialsArtifact)
        return [{ id: item.id, ...chapterLabel, artifact: billOfMaterialsArtifact }];
      const prepared = preparedChapters.find((chapter) => chapter.id === item.id);
      return prepared ? [{ ...prepared, ...chapterLabel }] : [];
    });
  if (!chapters.length) return;
  // Single chapters use the same document assembly, including navigation and notes.
  // Their content has already passed through the chapter generator's license policy.

  onProgress?.(80, "PDF-Kapitel werden vorbereitet …");
  const sources = await Promise.all(
    chapters.map(async (chapter) => ({
      ...chapter,
      document: await PDFDocument.load(await chapter.artifact.blob.arrayBuffer()),
    })),
  );
  let contentPageIndex = 0;
  const chapterEntries = sources.map((source) => {
    const chapterCoverPageIndex = contentPageIndex;
    const chapterContentPageIndex = chapterCoverPageIndex + 1;
    const navigation = source.artifact.tocEntries ?? source.artifact.pages;
    const sectionKeys = new Set(
      navigation.filter((page) => page.sectionTitle).map((page) => page.sectionId ?? page.sectionTitle!),
    );
    const sectionEntries = Array.from(sectionKeys).map((key) => {
      const pages = navigation.filter((page) => (page.sectionId ?? page.sectionTitle) === key);
      const first = pages[0];
      return {
        title: first.sectionTitle!,
        contentPageIndex: chapterContentPageIndex + first.pageIndex,
        pageLabel: first.pageLabel,
        children: pages
          .filter((page) => !page.omitNavigationChild && !page.title.endsWith("\u00b7 Fortsetzung"))
          .map((page) => ({
            title: page.title,
            contentPageIndex: chapterContentPageIndex + page.pageIndex,
            pageLabel: page.pageLabel,
          })),
      };
    });
    const unsectionedPages = navigation.filter((page) => !page.sectionTitle);
    const entry = {
      title: source.title,
      contentPageIndex: chapterCoverPageIndex,
      sections: sectionEntries,
      children: unsectionedPages.map((page) => ({
        title: page.title,
        contentPageIndex: chapterContentPageIndex + page.pageIndex,
        pageLabel: page.pageLabel,
      })),
    };
    contentPageIndex += 1 + source.document.getPageCount();
    return entry;
  });
  const entries = chapterEntries.flatMap((entry) => [
    { title: entry.title, contentPageIndex: entry.contentPageIndex, level: 1 as const },
    ...entry.sections.flatMap((section) => [
      {
        title: section.title,
        contentPageIndex: section.contentPageIndex,
        pageLabel: section.pageLabel,
        level: 2 as const,
      },
      ...section.children.map((child) => ({ ...child, level: 3 as const })),
    ]),
    ...entry.children
      .filter((child) => !child.title.endsWith("· Fortsetzung"))
      .map((child) => ({ ...child, level: 2 as const })),
  ]);
  const toc = await createC1PdfTableOfContentsBlob({
    entries: entries.map(entry => ({ ...entry, pageLabel: undefined })),
    projectName, pageSize: "A4", orientation: "portrait", pagesBeforeContent: 3,
    globalPageNumbers: true,
  });
  const brand = resolvePdfDocumentBrand(state);
  const substitutionValues = {
    BrandName: brand.name,
    ProductName: mode === "main" ? "Projekt-Dokumentation" : "Concept One",
    UserName: state.settings?.userName || brand.name,
  };
  const cover = await createC1MarkdownCoverPdfBlob({
    markdownTemplate: C1_COVER_MARKDOWN,
    substitutionValues,
    documentName: "Projekt-Dokumentation",
    projectName: projectName,
    dateText: new Intl.DateTimeFormat("de-DE").format(new Date()),
    logo: ESTADA_PDF_COVER_LOGO,
    pageSize: "A4",
    orientation: "portrait",
  });
  const documentation = await createC1MarkdownCoverPdfBlob({
    documentName: "Projekt-Dokumentation",
    markdownTemplate:
      mode === "main"
        ? "# Projektdokumentation\n\nDiese Dokumentation enthält die ausgewählten Listen des aktuellen ESTADA-Projekts zum angegebenen Erstellungsdatum."
        : getC1DocumentationMarkdown(),
    textAlign: "left",
    projectName: projectName,
    dateText: new Intl.DateTimeFormat("de-DE").format(new Date()),
    logo: brand.logoSource,
    pageSize: "A4",
    orientation: "portrait",
  });
  onProgress?.(88, "Kapitel-Trennblätter werden erstellt …");
  const chapterCovers = await Promise.all(
    sources.map((source) =>
      createC1MarkdownCoverPdfBlob({
        documentName: source.title,
        markdownTemplate: C1_CHAPTER_COVER_MARKDOWN,
        substitutionValues: { ...substitutionValues, ChapterNumber: String(source.chapterNumber), ChapterTitle: source.chapterTitle },
        projectName: projectName,
        dateText: new Intl.DateTimeFormat("de-DE").format(new Date()),
        logo: brand.logoSource,
        pageSize: "A4",
        orientation: "portrait",
      }),
    ),
  );
  const merged = await PDFDocument.create();
  const generalNotes = await createGeneralNotesPdf(state, chapters.map(chapter => chapter.id), projectName);
  for (const blob of [cover, documentation, toc.blob, generalNotes]) {
    const source = await PDFDocument.load(await blob.arrayBuffer());
    const pages = await merged.copyPages(source, source.getPageIndices());
    pages.forEach((page) => merged.addPage(page));
  }
  onProgress?.(94, "PDF wird zusammengeführt …");
  for (const [index, source] of sources.entries()) {
    const chapterCover = await PDFDocument.load(await chapterCovers[index].arrayBuffer());
    const coverPages = await merged.copyPages(chapterCover, chapterCover.getPageIndices());
    coverPages.forEach((page) => merged.addPage(page));
    const pages = await merged.copyPages(source.document, source.document.getPageIndices());
    pages.forEach((page) => merged.addPage(page));
  }
  const contentOffset = 3 + toc.pageCount;
  addC1PdfOutlines(merged, [
    { title: "Deckblatt", pageIndex: 0 },
    { title: "Bemerkungen", pageIndex: 1 },
    { title: "Inhaltsverzeichnis", pageIndex: 2 },
    { title: "Allgemeine Hinweise", pageIndex: 2 + toc.pageCount },
    ...chapterEntries.map((entry) => ({
      title: entry.title,
      pageIndex: contentOffset + entry.contentPageIndex,
      children: entry.children
        .filter((child) => !child.title.endsWith("· Fortsetzung"))
        .map((child) => ({
          title: child.title,
          pageIndex: contentOffset + child.contentPageIndex,
        })),
      ...(entry.sections.length
        ? {
            children: [
              ...entry.sections.map((section) => ({
                title: section.title,
                pageIndex: contentOffset + section.contentPageIndex,
                children: section.children.map((child) => ({
                  title: child.title,
                  pageIndex: contentOffset + child.contentPageIndex,
                })),
              })),
              ...entry.children
                .filter((child) => !child.title.endsWith("· Fortsetzung"))
                .map((child) => ({ title: child.title, pageIndex: contentOffset + child.contentPageIndex })),
            ],
          }
        : {}),
    })),
  ]);
  addC1PdfPageLinks(
    merged,
    toc.links.map((link) => ({
      ...link,
      sourcePageIndex: 2 + link.tableOfContentsPageIndex,
      targetPageIndex: contentOffset + link.contentPageIndex,
    })),
  );
  // Resolve chapter-local navigation after copying pages: source PDF destinations cannot
  // be relied on across merging, chapter covers and license-preview replacement pages.
  let chapterOffset = contentOffset;
  for (const source of sources) {
    chapterOffset += 1;
    const targets = new Map<string, number>();
    for (const page of source.artifact.pages) {
      for (const id of page.sourceIds ?? []) targets.set(id, chapterOffset + page.pageIndex);
    }
    for (const page of source.artifact.pages) {
      for (const link of page.links ?? []) {
        const targetPageIndex = targets.get(link.targetId);
        if (targetPageIndex === undefined) continue;
        addC1PdfPageLinks(merged, [{ ...link, sourcePageIndex: chapterOffset + page.pageIndex, targetPageIndex }]);
      }
    }
    chapterOffset += source.document.getPageCount();
  }
  const floorPlanSource = sources.find((source) => source.id === "floorPlans");
  const roomPlanSource = sources.find((source) => source.id === "roomPlans");
  if (floorPlanSource && roomPlanSource) {
    const chapterStartPage = (chapterId: PdfChapterId) => {
      let pageIndex = contentOffset;
      for (const source of sources) {
        pageIndex += 1; // chapter cover
        if (source.id === chapterId) return pageIndex;
        pageIndex += source.document.getPageCount();
      }
      return -1;
    };
    const floorStartPage = chapterStartPage("floorPlans");
    const roomStartPage = chapterStartPage("roomPlans");
    const targetPages = new Map<string, number>();
    roomPlanSource.artifact.pages.forEach((page) => {
      page.sourceIds?.forEach((id) => targetPages.set(id, roomStartPage + page.pageIndex));
    });
    addC1PdfPageLinks(
      merged,
      floorPlanSource.artifact.pages.flatMap((page) =>
        (page.links ?? []).flatMap((link) => {
          const targetPageIndex = targetPages.get(link.targetId);
          return targetPageIndex === undefined
            ? []
            : [{ ...link, sourcePageIndex: floorStartPage + page.pageIndex, targetPageIndex }];
        }),
      ),
    );
  }
  await deduplicateC1PdfImages(merged);
  await addDocumentPageNumbers(merged);
  const bytes = await merged.save();
  const blob = new Blob([Uint8Array.from(bytes)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  const selectedChapter = config.chapters.find(chapter => chapter.id === singleChapterId);
  link.download = c1PdfFileName(selectedChapter ? `${projectName}_${pdfChapterTitle(selectedChapter)}` : projectName);
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  onProgress?.(100, "Master-PDF wurde erstellt.");
}
